import {
  BillingRequestCollectBankAccountRequestBody,
  BillingRequestCollectBankAccountResponseBody,
  BillingRequestCollectCustomerDetailsRequestBody,
  BillingRequestCollectCustomerDetailsResponseBody,
  BillingRequestResource,
  BillingRequestSelfResponseBody,
  CustomerVerificationVerificationMethod as VerificationMethod,
  UiComponentBootstrapResponseBody,
} from "@gocardless/api/dashboard/types";

import { Core } from "../core";
import { Locale } from "../i18n";

import { Environment } from "./environments";
import { OnStepErrorHandler } from "./errors";

import type { GoCardlessEvent, PublicEvents } from "../bus";
import { Variant } from "@src/components/checkout/billing-request/schemes/config";

export interface GoCardlessConfig {
  environment: Environment;
  sessionToken: () => Promise<string>;
  appearance?: Appearance;
  components?: GcComponent[];
  enableAnalytics?: boolean;
  enableErrorTelemetry?: boolean;
  onEvent?: (event: GoCardlessEvent) => void;
  onError?: (error: GoCardlessError) => void;
  // Controls both the UI language and the `Accept-Language` header sent
  // with API requests, so backend-localised validation errors match it.
  // Defaults to DEFAULT_LOCALE (see ../i18n) when omitted.
  locale?: Locale;
}

export interface MountedComponent {
  mount(target: string): Promise<void>;
  unmount(): void;
}

// Maps each createComponent name to its specific config type. Every value
// here is expected to extend ComponentConfig
// Add an entry here whenever a name is added to registry.ts
export interface ComponentConfigMap {
  "billing-request": BillingRequestConfig;
  "remember-me-signup": RememberMeSignUpConfig;
  "remember-me-return-flow": RememberMeReturnFlowConfig;
}

export type ComponentFactory<Name extends keyof ComponentConfigMap> = (
  config: ComponentConfigMap[Name]
) => GcComponent;

export type ComponentLoader<Name extends keyof ComponentConfigMap> =
  () => Promise<ComponentFactory<Name>>;

export interface GoCardlessInstance {
  unmountAll(): void;
  createComponent<Name extends keyof ComponentConfigMap>(
    name: Name,
    config?: ComponentConfigMap[Name]
  ): MountedComponent;
}

export type GcComponent = {
  name: string;
  mount: (core: Core, target?: string) => void;
  unmount: () => void;

  // The component is responsible for defining what public events it emits,
  // and the sdk will forward those events to the onEvent callback.
  publicEvents?: (keyof PublicEvents)[];

  onError?: (error: GoCardlessError) => void;

  // Bootstrap data keys this component needs resolved - the SDK fetches
  // these from a shared, deduped store (see ../bootstrap.ts) that batches
  // concurrent requests together and caches already-fetched keys, and hands
  // each component only what it asked for.
  bootstrapDataKeys?: (keyof UiComponentBootstrapResponseBody)[];
};

export interface ComponentConfig {
  target?: string;
  appearance?: Appearance;
  onError?: (error: GoCardlessError) => void;
}

export interface GoCardlessErrorDetail {
  request_pointer: string;
  message: string;
  reason?: string;
}

export interface GoCardlessError {
  code: number;
  message: string;
  isUnrecoverable: boolean;
  type?: string;
  requestId?: string;
  errors?: GoCardlessErrorDetail[];
}

export interface Appearance {
  backgroundColor?: string;
  bodyFontSize?: string;
  bodyFontWeight?: string;
  borderColor?: string;
  buttonBorderRadius?: string;
  buttonPadding?: string;
  buttonPrimaryColor?: string;
  buttonPrimaryHoverBackgroundColor?: string;
  buttonPrimaryHoverTextColor?: string;
  buttonPrimaryTextColor?: string;
  buttonSubmitTextContent?: string;
  captionFontSize?: string;
  captionFontWeight?: string;
  checkboxBackgroundColor?: string;
  checkboxSize?: string;
  checkboxTextColor?: string;
  dropdownFooterBackgroundColor?: string;
  dropdownFooterBorderColor?: string;
  dropdownHoverBackgroundColor?: string;
  dropdownHoverTextColor?: string;
  dropdownPadding?: string;
  formValidationErrorColor?: string;
  formVerticalSpacing?: string;
  headerFontWeight?: string;
  inputBackgroundColor?: string;
  inputBorderRadius?: string;
  inputPadding?: string;
  inputPlaceholderColor?: string;
  inputTextColor?: string;
  labelTextColor?: string;
  linkHoverTextColor?: string;
  linkTextColor?: string;
  loadingSpinnerColor?: string;
  separatorColor?: string;
  textColor?: string;
  textFontFamily?: string;
  tagBackgroundColor?: string;
  tagBorderRadius?: string;
  tagPadding?: string;
  tagTextColor?: string;
  wrapperBorderColor?: string;
  wrapperBorderRadius?: string;
  wrapperBorderWidth?: string;
  wrapperMargin?: string;
}

export enum TextDecoration {
  None = "none",
  Underline = "underline",
}

export enum Scheme {
  Bacs = "bacs",
  SepaCore = "sepa_core",
  FasterPayments = "faster_payments",
}

export interface PrefilledCustomer {
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  email?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
}

export interface ErrorComponentProps {
  handleRetry?: () => void;
  canRetry: boolean;
  sessionTokenExpired?: boolean;
}

export interface BillingRequestConfig extends ComponentConfig {
  schemes?: Scheme[];
  creditorId?: string;
  prefilledCustomer?: PrefilledCustomer;
  onReadyToFulfil?: (billingRequestId: string) => void;
  // Attaches the component to a billing request the integrator already
  // created (e.g. server-side, from their own template/flow), instead of
  // having the component create a new one from `schemes`/`creditorId`.
  billingRequestId?: string;
}

// Not part of the public surface yet (MENG-3687, Pay by Bank is still in
// development) - kept separate from BillingRequestConfig so these fields
// don't appear in the published types/autocomplete. Internal code reads
// config as BillingRequestConfigInternal where it needs them.
export interface BillingRequestConfigInternal extends BillingRequestConfig {
  variant?: Variant;
}

export interface RememberMeSignUpConfig extends ComponentConfig {
  billingRequestId: string;
  email: string;
  onSuccess?: () => void;
}

export interface RememberMeSignUpRef {
  save: () => Promise<void>;
}

export interface RememberMeReturnFlowConfig extends ComponentConfig {
  billingRequestId: string;
  defaultEmail?: string;
  onChange?: (email: string) => void;
  onCustomerRecognitionCheck?: (result: CustomerRecognitionResult) => void;
  onVerificationAttempt?: (result: VerificationResult) => void;
  onVerificationExit?: (exitPoint: VerificationExitPoint) => void;
}

export interface CheckoutStepProps {
  onStepSuccess?: (billingRequest?: BillingRequestSelfResponseBody) => void;
  onStepError?: OnStepErrorHandler;
}

export interface CustomerDetailsFormRef {
  submit: (
    billingRequestId: string,
    data: BillingRequestCollectCustomerDetailsRequestBody
  ) => Promise<void | BillingRequestCollectCustomerDetailsResponseBody>;
}

export interface BankDetailsFormRef {
  submit: (
    billingRequestId: string,
    data: BillingRequestCollectBankAccountRequestBody
  ) => Promise<void | BillingRequestCollectBankAccountResponseBody>;
}

export interface CustomerRecognitionResult {
  recognised: boolean;
  customerData?: RecognisedCustomerData;
}

export enum FlowState {
  EmailEntry = "email_entry",
  CustomerRecognised = "customer_recognised",
  Verifying = "verifying",
  Verified = "verified",
  ManualEntry = "manual_entry",
}

export interface RecognisedCustomerData {
  email: string;
  phoneNumber?: string | null;
}

export enum VerificationExitPoint {
  UserClosed = "user_closed",
  VerificationFailed = "verification_failed",
  VerificationSuccess = "verification_success",
}

export interface VerificationResult {
  success: boolean;
  billingRequest?: BillingRequestResource;
  error?: Error;
}

export interface RememberedCustomerPromptCardProps {
  onVerificationInitiated?: () => void;
}

export interface InPageVerificationComponentProps {
  email: string;
  billingRequestId: string;
  phone?: string | null;
  defaultVerificationMethod?: VerificationMethod;
  onVerificationAttempt?: (result: VerificationResult) => void;
  onExit?: (exitPoint: VerificationExitPoint) => void;
}

export interface DebouncedCustomerEmailFieldProps {
  billingRequestId: string;
  defaultEmail?: string;
  onChange?: (email: string) => void;
  onCustomerRecognitionCheck?: (result: CustomerRecognitionResult) => void;
  onError?: (error: GoCardlessError) => void;
}
